import type { ComponentProps } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils.js';

/* Deliberately square-ish rather than a pill: the Swiss direction wants a
   label, not a sticker. */
export const badgeVariants = cva(
  cn(
    'inline-flex w-fit shrink-0 items-center justify-center gap-(--space-1) whitespace-nowrap',
    'rounded-(--radius-sm) px-(--space-2) py-(--space-1)',
    'text-[length:var(--text-micro)] font-medium tracking-wide uppercase',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3",
  ),
  {
    variants: {
      variant: {
        default: 'bg-accent-quiet text-accent',
        secondary: 'bg-muted text-ink-muted',
        outline: 'border border-rule-strong text-ink-muted',
        ok: 'bg-(--color-ok-quiet) text-ok',
        warn: 'bg-(--color-warn-quiet) text-warn',
        danger: 'bg-(--color-danger-quiet) text-danger',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'span';
  return (
    <Component
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}
